/**
 * SecureField — drop-in TextInput replacement for the three encrypted columns
 * (bank account number, routing number, vendor tax ID).
 *
 * Behavior rules (per spec):
 *  - Field is ALWAYS hidden on mount. Even if the unlock context is unlocked,
 *    plaintext is never visible until the user clicks the eye icon. Walking
 *    past a screen never reveals an SSN.
 *  - Clicking the eye while LOCKED opens the unlock modal. After successful
 *    unlock, the field flips to visible plaintext.
 *  - Clicking the eye again hides it back to `••••••`.
 *  - On blur (when visible), the plaintext is encrypted and bubbled up via
 *    `onChange`, along with the computed mask (last-4 or `***-**-1234`).
 *  - When the context idle-locks (5 min), this field re-masks automatically.
 *
 * Output contract:
 *  - `value` is always the ciphertext (`sf1:...`) or empty string. The parent
 *    persists this to the API verbatim.
 *  - `onChange({ ciphertext, mask })` fires once per save (debounced via blur),
 *    so the parent can keep both columns in sync without re-encrypting on
 *    every keystroke.
 */

import { Ionicons } from '@expo/vector-icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';


import { SecureUnlockModal } from './secure-unlock-modal';

import { Text, TextInput } from '@/components/ui';
import { useSecureUnlock } from '@/services/secure-unlock-context';
import { isEncryptedBlob } from '@/utils/secure-crypto';

export type SecureMaskStrategy = 'last4' | 'taxId';

export interface SecureFieldChange {
	/** `sf1:...` blob to persist. Empty string when the field is cleared. */
	ciphertext: string;
	/** Plaintext-safe mask the parent stores alongside (e.g. `***-**-1234`). */
	mask: string;
}

export interface SecureFieldProps {
	label: string;
	/** Existing ciphertext from the API, or empty if unset. */
	value: string;
	/** Optional mask the API has stored — shown while locked so the user can identify the field. */
	storedMask?: string | null;
	/** Decides how to compute the mask sent back to the parent on save. */
	maskStrategy: SecureMaskStrategy;
	onChange: (next: SecureFieldChange) => void;
	disabled?: boolean;
	placeholder?: string;
	helperText?: string;
}

const computeMask = (plaintext: string, strategy: SecureMaskStrategy): string => {
	if (!plaintext) return '';
	const digits = plaintext.replace(/\D/g, '');
	switch (strategy) {
		case 'last4': {
			const last4 = digits.slice(-4);
			return last4 ? `••••${last4}` : '';
		}
		case 'taxId': {
			const last4 = digits.slice(-4);
			return last4 ? `***-**-${last4}` : '';
		}
	}
};

export const SecureField: React.FC<SecureFieldProps> = ({
	label,
	value,
	storedMask,
	maskStrategy,
	onChange,
	disabled = false,
	placeholder,
	helperText,
}) => {
	const { isUnlocked, isReady, encrypt, decrypt } = useSecureUnlock();
	// When the field has no stored value yet, start in editable mode — there's
	// no plaintext to hide and forcing an unlock just to enter a brand-new SSN
	// is friction without security benefit. The unlock prompt only fires at
	// save time, and only if the context isn't already unlocked.
	const hasStoredValue = !!value;
	const [revealed, setRevealed] = useState(!hasStoredValue);
	const [plain, setPlain] = useState('');
	const [unlockOpen, setUnlockOpen] = useState(false);
	const [decryptError, setDecryptError] = useState<string | null>(null);
	// Holds the plaintext we still owe an encrypt+onChange for when the user
	// types into an empty field while locked. After they unlock, we drain it.
	const pendingPlainRef = React.useRef<string | null>(null);

	// Auto-mask whenever the context locks — but only when there IS a stored
	// value. If the user is mid-entry on a never-saved field, locking
	// shouldn't blow away their typed input.
	useEffect(() => {
		if (!isUnlocked && revealed && hasStoredValue) {
			// Preserve any in-progress typing across the lock. The user can
			// re-unlock and onUnlocked() will drain it back to onChange via
			// pendingPlainRef. Without this, a silent idle-lock mid-edit
			// loses whatever was typed since the last blur.
			if (plain) pendingPlainRef.current = plain;
			setRevealed(false);
			setPlain('');
		}
	}, [isUnlocked, revealed, hasStoredValue, plain]);

	// When revealing a stored value for the first time, decrypt the blob.
	useEffect(() => {
		if (!revealed) return;
		if (!value) return;
		if (!isEncryptedBlob(value)) {
			// Legacy plaintext fallback — show as-is so the user can re-save and trigger encryption.
			setPlain(value);
			setDecryptError(null);
			return;
		}
		try {
			setPlain(decrypt(value));
			setDecryptError(null);
		} catch (err) {
			setDecryptError(err instanceof Error ? err.message : 'Could not decrypt');
			setPlain('');
		}
	}, [revealed, value, decrypt]);

	const maskedDisplay = useMemo(() => {
		if (storedMask) return storedMask;
		if (value) return '••••••••';
		return '';
	}, [storedMask, value]);

	const onEyePress = useCallback(() => {
		if (disabled) return;
		if (revealed) {
			// Hide — only meaningful when something is stored, otherwise leave
			// the empty field open for editing.
			if (!hasStoredValue) return;
			setRevealed(false);
			setPlain('');
			return;
		}
		if (!isReady) {
			// No password ever set; nothing to unlock against. Surface a hint by
			// opening the modal anyway — the modal handles the "not ready" state.
			setUnlockOpen(true);
			return;
		}
		if (!isUnlocked) {
			setUnlockOpen(true);
			return;
		}
		setRevealed(true);
	}, [disabled, isReady, isUnlocked, revealed, hasStoredValue]);

	const flushEncryptedSave = useCallback((next: string) => {
		try {
			const ct = encrypt(next);
			onChange({ ciphertext: ct, mask: computeMask(next, maskStrategy) });
			pendingPlainRef.current = null;
			setDecryptError(null);
		} catch (err) {
			setDecryptError(err instanceof Error ? err.message : 'Could not encrypt');
		}
	}, [encrypt, maskStrategy, onChange]);

	const onUnlocked = useCallback(() => {
		// If the user typed into an empty field while locked, we owe the
		// parent the encrypted result now that we have a key.
		const pending = pendingPlainRef.current;
		if (pending) {
			flushEncryptedSave(pending);
		}
		setRevealed(true);
	}, [flushEncryptedSave]);

	const onBlur = useCallback(() => {
		if (!revealed) return;
		const trimmed = plain;
		if (!trimmed) {
			onChange({ ciphertext: '', mask: '' });
			pendingPlainRef.current = null;
			return;
		}
		if (!isUnlocked || !isReady) {
			// Defer the encryption until the user provides the password. The
			// plaintext stays in component state (and the input) so it's not
			// lost; we just don't push it to the parent until we can encrypt.
			pendingPlainRef.current = trimmed;
			if (isReady) setUnlockOpen(true);
			return;
		}
		flushEncryptedSave(trimmed);
	}, [revealed, plain, isUnlocked, isReady, onChange, flushEncryptedSave]);

	const eyeIcon = revealed ? 'eye-off-outline' : 'eye-outline';
	const fallbackError = decryptError ?? helperText;

	return (
		<View>
			{revealed ? (
				<TextInput
					label={label}
					value={plain}
					onChangeText={setPlain}
					onBlur={onBlur}
					autoCorrect={false}
					autoCapitalize="none"
					editable={!disabled}
					placeholder={placeholder}
					helperText={fallbackError ?? undefined}
					right={
						<Pressable onPress={onEyePress} hitSlop={8} accessibilityLabel="Hide value">
							<Ionicons name={eyeIcon} size={20} color="#546e7a" />
						</Pressable>
					}
				/>
			) : (
				<View style={styles.lockedRow}>
					<View style={{ flex: 1 }}>
						<Text style={styles.label}>{label}</Text>
						<View style={styles.maskedBox}>
							<Text style={styles.maskedText}>
								{maskedDisplay || (placeholder ? `(${placeholder})` : '(not set)')}
							</Text>
							<Pressable onPress={onEyePress} hitSlop={8} accessibilityLabel="Reveal value" disabled={disabled}>
								<Ionicons name={eyeIcon} size={20} color={disabled ? '#cfd8dc' : '#1976d2'} />
							</Pressable>
						</View>
						{fallbackError ? <Text style={styles.helper}>{fallbackError}</Text> : null}
					</View>
				</View>
			)}
			<SecureUnlockModal
				visible={unlockOpen}
				onDismiss={() => setUnlockOpen(false)}
				onUnlocked={onUnlocked}
				message={`Enter the sensitive-data password to reveal ${label}.`}
			/>
		</View>
	);
};

const styles = StyleSheet.create({
	lockedRow: { flexDirection: 'row', gap: 8, alignItems: 'flex-start' },
	label: { fontSize: 11, color: '#546e7a', fontWeight: '600', textTransform: 'uppercase', marginBottom: 4 },
	maskedBox: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 8,
		borderWidth: 1,
		borderColor: '#cfd8dc',
		borderRadius: 6,
		paddingHorizontal: 12,
		paddingVertical: 12,
		backgroundColor: '#fafafa',
	},
	maskedText: { flex: 1, fontSize: 14, color: '#37474f', letterSpacing: 1 },
	helper: { fontSize: 11, color: '#78909c', marginTop: 4 },
});
